import { ListCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader } from '~/components/ui/card';
import type { AttributeCoverageRow } from '~/features/catalog-operations/types/catalog-operations.types';

import { CoverageBar } from '../coverage-bar';

interface Props {
  rows: AttributeCoverageRow[];
}

export function AttributeCoverageList({ rows }: Props) {
  const { t } = useTranslation('catalog-operations');
  return (
    <Card data-testid="batch-detail-attribute-coverage-card">
      <CardHeader className="flex flex-row items-center gap-4 p-6">
        <div className="bg-primary/10 text-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
          <ListCheck className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-0.5">
          <h2 data-testid="batch-detail-attribute-coverage-title" className="text-xl font-medium">
            {t('attributeCoverage.title')}
          </h2>
          <p className="text-muted-foreground text-sm font-light">
            {t('attributeCoverage.subtitle')}
          </p>
        </div>
      </CardHeader>

      <CardContent className="bg-primary/4 mr-6 mb-6 ml-6 max-h-65 overflow-y-auto rounded-xl p-3">
        <ul data-testid="batch-detail-attribute-coverage-list">
          {rows.map((row) => (
            <li
              key={row.label}
              data-testid={`batch-detail-attribute-coverage-item-${row.label}`}
              className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto_auto] items-center gap-4 px-3 py-3"
            >
              <div className="flex flex-wrap items-center">
                <span className="text-sm capitalize">{(row.label || '').replaceAll('-', ' ')}</span>
              </div>
              <CoverageBar percent={row.percent} />
              <span className="text-muted-foreground text-sm tabular-nums">
                <span className="text-foreground font-semibold">
                  {row.current.toLocaleString()}
                </span>
                /{row.total.toLocaleString()}
              </span>
              <span
                data-testid={`batch-detail-attribute-coverage-item-value-${row.label}`}
                className="w-12 text-right text-sm font-bold tabular-nums"
              >
                {row.percent}%
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
